import { GuestSearchResult } from './searchGuests';
import firebase from 'utils/firebase';

const INDEX_NAME = 'guests';

// Endpoint that mints a per-restaurant Meilisearch tenant token.
// The token is scoped server-side to `restaurantId = "<id>"` so the browser
// can't widen the filter to query other restaurants.
const TENANT_TOKEN_ENDPOINT = `https://europe-west3-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/getMeilisearchTenantToken`;

interface CachedToken {
  token: string;
  host: string;
  indexName: string;
  fetchedAt: number;
}

// Tenant tokens have no expiry today, but cache by restaurantId per session
// so we never block search on a network round-trip after the first call.
const tokenCache: Record<string, CachedToken> = {};
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min — refresh before any future expiry policy

export interface MeilisearchParams {
  restaurantId: string;
  search: string;
  page?: number;
  perPage?: number;
  /** Restaurant country code (e.g. "de", "at", "ch") for phone normalisation */
  country?: string;
}

const COUNTRY_DIAL_CODE: Record<string, string> = {
  de: '49',
  at: '43',
  ch: '41',
};

/**
 * Detect phone-number-like tokens in the query and normalise them
 * to E.164 digit sequences so they match what Meilisearch has stored.
 *
 * Example (DE): "Leon 01785314202" → "Leon 491785314202"
 * Example (AT): "Leon 06641234567" → "Leon 436641234567"
 */
function normalizePhoneInQuery(query: string, country = 'de'): string {
  const dialCode = COUNTRY_DIAL_CODE[country] || '49';

  return query
    .trim()
    .split(/\s+/)
    .map((word) => {
      const cleaned = word.replace(/[-/()]/g, '');
      if (!/^\+?\d{6,}$/.test(cleaned)) return word;
      if (cleaned.startsWith('0') && !cleaned.startsWith('00'))
        return dialCode + cleaned.substring(1);
      if (cleaned.startsWith('+')) return cleaned.substring(1);
      return cleaned;
    })
    .join(' ');
}

async function fetchTenantToken(restaurantId: string): Promise<CachedToken> {
  const cached = tokenCache[restaurantId];
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached;
  }

  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    throw new Error('Not signed in — cannot fetch Meilisearch tenant token');
  }
  const idToken = await currentUser.getIdToken();

  const response = await fetch(TENANT_TOKEN_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ restaurantId }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Tenant token endpoint ${response.status}: ${text.slice(0, 200)}`,
    );
  }

  const data = await response.json();
  if (!data.success || !data.token || !data.host) {
    throw new Error(
      `Tenant token response missing fields: ${JSON.stringify(data)}`,
    );
  }

  const entry: CachedToken = {
    token: data.token,
    host: data.host,
    indexName: data.indexName ?? INDEX_NAME,
    fetchedAt: Date.now(),
  };
  tokenCache[restaurantId] = entry;
  return entry;
}

/**
 * Search guests via Meilisearch Cloud REST API using a per-restaurant tenant
 * token fetched from gastronaut-api. The token is filter-scoped server-side
 * so the browser can't search other restaurants. Used for v02 restaurants
 * (with guestbook app — i.e. all post-migration restaurants).
 */
const searchGuestsMeilisearch = async ({
  restaurantId,
  search,
  page = 0,
  perPage = 10,
  country = 'de',
}: MeilisearchParams): Promise<GuestSearchResult[]> => {
  if (!search || search.length < 2) {
    return [];
  }
  if (!restaurantId) {
    return [];
  }

  try {
    const { token, host, indexName } = await fetchTenantToken(restaurantId);

    const response = await fetch(`${host}/indexes/${indexName}/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        q: normalizePhoneInQuery(search, country),
        // Tenant token already scopes to this restaurantId, but include it
        // explicitly so a stale cached token for the wrong restaurant fails fast.
        filter: `restaurantId = "${restaurantId}"`,
        limit: perPage,
        offset: page * perPage,
      }),
    });

    if (response.status === 401 || response.status === 403) {
      // Token may be expired/invalidated — drop cache and retry once.
      delete tokenCache[restaurantId];
      const retry = await fetchTenantToken(restaurantId);
      const retryResponse = await fetch(
        `${retry.host}/indexes/${retry.indexName}/search`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${retry.token}`,
          },
          body: JSON.stringify({
            q: normalizePhoneInQuery(search, country),
            filter: `restaurantId = "${restaurantId}"`,
            limit: perPage,
            offset: page * perPage,
          }),
        },
      );
      if (!retryResponse.ok) {
        throw new Error(
          `Meilisearch error after token refresh: ${retryResponse.status}`,
        );
      }
      const retryResult = await retryResponse.json();
      return mapHits(retryResult.hits || []);
    }

    if (!response.ok) {
      throw new Error(`Meilisearch error: ${response.status}`);
    }

    const result = await response.json();
    return mapHits(result.hits || []);
  } catch (error: any) {
    console.error('Meilisearch search error:', error.message);
    throw error;
  }
};

function mapHits(hits: any[]): GuestSearchResult[] {
  return hits.map((hit) => ({
    // Meilisearch primary key is "<guestId>-<restaurantId>". Downstream code
    // expects the composite as `id` (matches what v01 also returned).
    id: hit.id,
    name: hit.name || '',
    email: hit.email || '',
    phone: hit.phone || '',
    attr: hit.attr || [],
    lastReservation: hit.lastReservation || '',
    visits: hit.confirmedReservations || 0,
    vip: hit.attr?.includes('vip') || false,
    regular: hit.attr?.includes('regular') || false,
    blacklist: hit.attr?.includes('blacklist') || false,
  }));
}

export default searchGuestsMeilisearch;
